(() => {
  'use strict';

  const FORM_SELECTOR =
    '.section-header .gowin-header-search .gowin-search-form, ' +
    '.section-header .gowin-mobile-search .gowin-search-form';
  const MAX_PRODUCTS = 10;
  const REQUEST_LIMIT = 10;
  const cache = new Map();
  const STOP_WORDS = new Set([
    'de', 'del', 'la', 'las', 'el', 'los', 'y', 'con', 'para', 'por', 'en',
    'un', 'una', 'unos', 'unas', 'the', 'of', 'with', 'and', 'for'
  ]);
  const BOX_PATTERN = /\b(caja|box|case|display)\b/;
  const PACK_PATTERN = /\b(pack|bundle|kit|combo|set)\b/;

  let instanceCount = 0;

  const searchIcon = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="1.8"></circle>
      <path d="M16 16l4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
    </svg>
  `;

  const closeIcon = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
    </svg>
  `;

  const getConfig = () => {
    const element = document.querySelector('[data-gowin-search-config]');
    return {
      enabled: element?.dataset.enabled === 'true',
      endpoint: element?.dataset.endpoint || window.routes?.predictive_search_url || ''
    };
  };

  const normalizeQuery = (value) =>
    String(value || '').replace(/\s+/g, ' ').trim();

  const normalizeText = (value) =>
    normalizeQuery(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const queryTokens = (value) =>
    normalizeText(value)
      .split(' ')
      .filter((token) => token && !STOP_WORDS.has(token));

  const presentationType = (value) => {
    const normalized = normalizeText(value);
    if (PACK_PATTERN.test(normalized)) return 'pack';
    if (BOX_PATTERN.test(normalized)) return 'box';
    return 'single';
  };

  const explicitPresentation = (value) => {
    const normalized = normalizeText(value);
    if (PACK_PATTERN.test(normalized)) return 'pack';
    if (BOX_PATTERN.test(normalized)) return 'box';
    return '';
  };

  const canonicalHref = (href) => {
    try {
      const url = new URL(href || '', window.location.origin);
      return url.pathname.replace(/\/$/, '');
    } catch (_) {
      return String(href || '').split('#')[0].split('?')[0].replace(/\/$/, '');
    }
  };

  const namespaceMarkup = (markup, prefix) => {
    const template = document.createElement('template');
    template.innerHTML = markup;
    const ids = new Map();

    template.content.querySelectorAll('[id]').forEach((element) => {
      const current = element.id;
      if (!current) return;

      const next = `${prefix}-${current}`;
      ids.set(current, next);
      element.id = next;
    });

    [
      'aria-controls',
      'aria-labelledby',
      'aria-owns',
      'aria-activedescendant'
    ].forEach((attribute) => {
      template.content.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const value = element.getAttribute(attribute);
        if (!value) return;

        const next = value
          .split(/\s+/)
          .map((token) => ids.get(token) || token)
          .join(' ');

        element.setAttribute(attribute, next);
      });
    });

    return template.innerHTML;
  };

  const parseProductCandidates = (markup, source = 'base') => {
    const template = document.createElement('template');
    template.innerHTML = markup;
    const list = template.content.querySelector('#predictive-search-results-products-list');
    if (!list) return { template, list: null, candidates: [] };

    const candidates = Array.from(list.children).filter((node) => node.tagName === 'LI').map((node, index) => {
      const anchor = node.querySelector('a[href]');
      const title = node.querySelector('.predictive-search__item-heading')?.textContent || '';
      const href = anchor?.getAttribute('href') || '';
      const normalizedTitle = normalizeText(title);
      const normalizedHref = normalizeText(href);
      const haystack = `${normalizedTitle} ${normalizedHref}`.trim();

      return {
        node,
        title,
        href,
        key: canonicalHref(href) || `${source}:${index}:${normalizedTitle}`,
        haystack,
        type: presentationType(`${title} ${href}`),
        source,
        sourceRank: index,
        score: 0,
        coverage: 0
      };
    });

    return { template, list, candidates };
  };

  const scoreCandidate = (candidate, query, requestedPresentation) => {
    const normalizedQuery = normalizeText(query);
    const tokens = queryTokens(query);
    if (!normalizedQuery || !tokens.length || !candidate.haystack) return -Infinity;

    const matched = tokens.filter((token) => candidate.haystack.includes(token));
    const coverage = matched.length / tokens.length;
    candidate.coverage = coverage;

    const minimumMatches = tokens.length <= 2 ? tokens.length : tokens.length - 1;
    if (matched.length < minimumMatches) return -Infinity;

    const title = normalizeText(candidate.title);
    let score = candidate.source === 'base' ? 42 : 18;

    if (title === normalizedQuery) score += 160;
    else if (title.startsWith(normalizedQuery)) score += 132;
    else if (title.includes(normalizedQuery)) score += 112;
    else if (candidate.haystack.includes(normalizedQuery)) score += 92;

    score += coverage * 78;
    score += Math.max(0, (candidate.source === 'base' ? 30 : 18) - candidate.sourceRank * 2);

    if (requestedPresentation) {
      score += candidate.type === requestedPresentation ? 72 : -34;
    } else if (candidate.type !== 'single') {
      score += 5;
    }

    if (candidate.source === candidate.type) score += 12;

    return score;
  };

  const selectDiverseProducts = (candidates, query) => {
    const requestedPresentation = explicitPresentation(query);
    const byKey = new Map();

    candidates.forEach((candidate) => {
      candidate.score = scoreCandidate(candidate, query, requestedPresentation);
      if (!Number.isFinite(candidate.score)) return;

      const existing = byKey.get(candidate.key);
      if (!existing || candidate.score > existing.score) {
        byKey.set(candidate.key, candidate);
      }
    });

    const ranked = Array.from(byKey.values()).sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.source === 'base' && b.source !== 'base') return -1;
      if (b.source === 'base' && a.source !== 'base') return 1;
      return a.sourceRank - b.sourceRank;
    });

    if (!ranked.length) return [];
    if (requestedPresentation) return ranked.slice(0, MAX_PRODUCTS);

    const selected = [];
    const selectedKeys = new Set();
    const push = (candidate) => {
      if (!candidate || selectedKeys.has(candidate.key) || selected.length >= MAX_PRODUCTS) return;
      selected.push(candidate);
      selectedKeys.add(candidate.key);
    };

    push(ranked[0]);

    const topScore = ranked[0].score;
    const groups = ['single', 'box', 'pack'];
    groups.forEach((group) => {
      if (selected.some((item) => item.type === group)) return;
      const candidate = ranked.find(
        (item) => item.type === group && item.score >= topScore - 74
      );
      push(candidate);
    });

    ranked.forEach(push);
    return selected;
  };

  const mergeProductMarkup = (baseMarkup, supplementalMarkups, query) => {
    const base = parseProductCandidates(baseMarkup, 'base');
    if (!base.list || !base.candidates.length) return baseMarkup;

    const allCandidates = [...base.candidates];
    supplementalMarkups.forEach(({ markup, source }) => {
      parseProductCandidates(markup, source).candidates.forEach((candidate) => {
        allCandidates.push(candidate);
      });
    });

    const selected = selectDiverseProducts(allCandidates, query);
    if (!selected.length) return baseMarkup;

    base.list.innerHTML = '';
    selected.forEach((candidate, index) => {
      const node = candidate.node.cloneNode(true);
      node.id = `predictive-search-option-product-${index + 1}`;
      node.setAttribute('aria-selected', 'false');
      base.list.appendChild(node);
    });

    return base.template.innerHTML;
  };

  const closeOtherForms = (currentForm) => {
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      if (form !== currentForm && typeof form.gowinSearchClose === 'function') {
        form.gowinSearchClose();
      }
    });
  };

  const buildRequestUrl = (endpoint, query) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', String(REQUEST_LIMIT));
    return url.toString();
  };

  const fetchMarkup = async (endpoint, query, signal) => {
    const response = await fetch(buildRequestUrl(endpoint, query), {
      signal,
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    });

    if (!response.ok) throw new Error('Predictive search request failed');

    const text = await response.text();
    const parsed = new DOMParser().parseFromString(text, 'text/html');
    return parsed.querySelector('#shopify-section-predictive-search')?.innerHTML?.trim() || '';
  };

  const expansionRequests = (baseMarkup, query) => {
    if (explicitPresentation(query)) return [];

    const baseCandidates = parseProductCandidates(baseMarkup, 'base').candidates;
    if (!baseCandidates.length) return [];

    const present = new Set(baseCandidates.map((candidate) => candidate.type));
    const requests = [];

    if (!present.has('box')) {
      requests.push({ source: 'box', query: `${query} caja` });
    }

    if (!present.has('pack')) {
      requests.push({ source: 'pack', query: `${query} pack` });
    }

    return requests;
  };

  const enhance = (form) => {
    const { enabled, endpoint } = getConfig();
    if (!enabled || !endpoint || form.dataset.gowinSearchEnhanced === 'true') return;

    const input = form.querySelector('input[type="search"][name="q"]');
    if (!input) return;

    form.dataset.gowinSearchEnhanced = 'true';
    form.classList.add('gowin-search-form--enhanced');

    const prefix = `gowin-search-${++instanceCount}`;
    const resultsId = `${prefix}-suggestions`;
    let debounceTimer = 0;
    let requestController = null;
    let requestSequence = 0;

    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', resultsId);

    if (!form.querySelector('input[name="options[prefix]"]')) {
      const prefixInput = document.createElement('input');
      prefixInput.type = 'hidden';
      prefixInput.name = 'options[prefix]';
      prefixInput.value = 'last';
      form.appendChild(prefixInput);
    }

    const controls = document.createElement('div');
    controls.className = 'gowin-search-controls';
    controls.innerHTML = `
      <button
        type="reset"
        class="gowin-search-control gowin-search-reset"
        aria-label="Limpiar búsqueda"
        hidden
      >${closeIcon}</button>
      <button
        type="submit"
        class="gowin-search-control gowin-search-submit"
        aria-label="Buscar productos"
      >${searchIcon}</button>
    `;

    const resetButton = controls.querySelector('.gowin-search-reset');
    form.appendChild(controls);

    const results = document.createElement('div');
    results.id = resultsId;
    results.className = 'predictive-search gowin-search-suggestions';
    results.setAttribute('aria-live', 'polite');
    results.setAttribute('aria-label', 'Sugerencias de búsqueda');
    results.hidden = true;
    form.appendChild(results);

    const updateResetButton = () => {
      resetButton.hidden = normalizeQuery(input.value).length === 0;
    };

    const clearSelection = () => {
      results.querySelectorAll('[aria-selected="true"]').forEach((element) => {
        element.setAttribute('aria-selected', 'false');
      });
      input.removeAttribute('aria-activedescendant');
    };

    const close = () => {
      window.clearTimeout(debounceTimer);
      requestSequence += 1;

      if (requestController) {
        requestController.abort();
        requestController = null;
      }

      clearSelection();
      results.hidden = true;
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    };

    form.gowinSearchClose = close;

    const openWith = (markup) => {
      closeOtherForms(form);
      clearSelection();
      results.innerHTML = namespaceMarkup(markup, prefix);
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const showLoading = () => {
      closeOtherForms(form);
      results.innerHTML = '<p class="gowin-search-loading">Buscando productos…</p>';
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const showEmpty = () => {
      closeOtherForms(form);
      results.innerHTML = '<p class="gowin-search-empty">No encontramos coincidencias. Prueba con otra palabra.</p>';
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const requestResults = async () => {
      const query = normalizeQuery(input.value);
      updateResetButton();
      clearSelection();

      if (query.length < 2) {
        close();
        return;
      }

      const cacheKey = normalizeText(query);
      if (cache.has(cacheKey)) {
        openWith(cache.get(cacheKey));
        return;
      }

      if (requestController) requestController.abort();
      requestController = new AbortController();
      const sequence = ++requestSequence;
      showLoading();

      try {
        const baseMarkup = await fetchMarkup(endpoint, query, requestController.signal);
        if (sequence !== requestSequence) return;

        if (!baseMarkup) {
          showEmpty();
          return;
        }

        const expansions = expansionRequests(baseMarkup, query);
        const supplemental = expansions.length
          ? await Promise.all(
              expansions.map(async (request) => {
                try {
                  return {
                    source: request.source,
                    markup: await fetchMarkup(endpoint, request.query, requestController.signal)
                  };
                } catch (error) {
                  if (error?.name === 'AbortError') throw error;
                  return { source: request.source, markup: '' };
                }
              })
            )
          : [];

        if (sequence !== requestSequence) return;

        const markup = mergeProductMarkup(
          baseMarkup,
          supplemental.filter((item) => item.markup),
          query
        );

        cache.set(cacheKey, markup);
        openWith(markup);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        close();
      } finally {
        if (sequence === requestSequence) requestController = null;
      }
    };

    const scheduleRequest = (delay = 240) => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(requestResults, delay);
    };

    const visibleOptions = () => Array.from(
      results.querySelectorAll('[role="option"]')
    ).filter((option) => option.offsetParent !== null);

    const moveSelection = (direction) => {
      const options = visibleOptions();
      if (!options.length || results.hidden) return;

      const selectedIndex = options.findIndex(
        (option) => option.getAttribute('aria-selected') === 'true'
      );
      const nextIndex = direction > 0
        ? (selectedIndex + 1) % options.length
        : (selectedIndex <= 0 ? options.length - 1 : selectedIndex - 1);

      clearSelection();

      const next = options[nextIndex];
      next.setAttribute('aria-selected', 'true');
      if (next.id) input.setAttribute('aria-activedescendant', next.id);
      next.scrollIntoView({ block: 'nearest' });
    };

    input.addEventListener('input', () => scheduleRequest());
    input.addEventListener('focus', () => {
      if (normalizeQuery(input.value).length >= 2) scheduleRequest(0);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveSelection(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (event.key === 'Enter') {
        const selected = results.querySelector('[aria-selected="true"]');
        const action = selected?.matches('a, button')
          ? selected
          : selected?.querySelector('a, button');

        if (action) {
          event.preventDefault();
          action.click();
        }
      }
    });

    form.addEventListener('reset', () => {
      window.setTimeout(() => {
        updateResetButton();
        close();
        input.focus();
      }, 0);
    });

    form.addEventListener('submit', (event) => {
      if (!normalizeQuery(input.value)) {
        event.preventDefault();
        input.focus();
        return;
      }

      close();
    });

    form.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!form.contains(document.activeElement)) close();
      }, 140);
    });

    results.addEventListener('click', (event) => {
      if (event.target.closest('a')) close();
    });

    updateResetButton();
  };

  const init = () => {
    const { enabled } = getConfig();
    if (!enabled) return;
    document.querySelectorAll(FORM_SELECTOR).forEach(enhance);
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init, { once: true })
    : init();

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.gowin-search-form--enhanced')) {
      closeOtherForms(null);
    }
  });
  window.addEventListener('pageshow', init);
})();
